import type { GoogleAuthInterface } from "./interface"
import { googleSdkLoaded } from "vue3-google-login"
import { useTokenCookie } from "../local/token-cookie"

type Google = Parameters<Parameters<typeof googleSdkLoaded>["0"]>["0"]

type ErrorPopupResponse = {
	message: string
	/** The error stack trace. */
	stack: string
	/** The error type, giving the detailed reason for the error. */
	type: string
}

export function useGoogleAuthWeb(): GoogleAuthInterface {
	const loading = ref(false)
	const api = useApi()
	const login = useLogin()
	const token = useTokenCookie()
	const notify = useNotification()
	const config = useRuntimeConfig()

	async function signIn(redirect: boolean = true) {
		loading.value = true
		const google = await initialize()

		try {
			await iframeLogin(google)
			if (redirect)
				await navigateTo("/app")
			loading.value = false
		}
		catch (e) {
			console.error(e)
			const error = e as ErrorPopupResponse
			if (error.type === "popup_failed_to_open") {
				notify.errorText({ description: "دسترسی باز کردن پنجره جدید، داده نشد!" })
			}
			else if (error.type === "popup_closed") {
				notify.errorText({ description: "پنجره ورود با گوگل بسته شد!" })
			}
			loading.value = false
		}
	}

	function iframeLogin(google: Google) {
		return new Promise<void>((resolve, reject) => {
			const client = google.accounts.oauth2.initTokenClient({
				client_id: config.public.google.clientId,
				scope: "https://www.googleapis.com/auth/userinfo.email",
				async callback({ access_token }) {
					const { data } = await api.user.exchangeGoogleToken({ code: access_token })
					token.setCookie(data.token)
					login.setUser(data.me)
					resolve()
				},
				error_callback(err) {
					reject(err)
				},
			})
			client.requestAccessToken()
		})
	}

	return {
		initialize,
		signIn,
		loading,
	}
}

async function initialize() {
	return new Promise<Google>((resolve) => {
		googleSdkLoaded(async (google) => {
			resolve(google)
		})
	})
}
